import React, { ReactNode, useCallback, useState, useMemo, useEffect } from 'react';
import {
    CatalogApiObject,
    CatalogsFull,
    DossierPrestationJsonldRead,
    FormulaireFormaliteApiObject,
    Prescripteur,
    selectedSousClientSelector,
    SousClientApi,
    SousClientMainSelector,
    TiersAppDispatch,
    useFormaliteService,
    useFormulaireService,
    useOffreService,
    useSociete,
    useTiersService,
    useTiersServiceSelector,
} from '@europrocurement/l2d-domain';
import { useDispatch } from 'react-redux';
import { castIriToId } from '@europrocurement/l2d-utils';
import { useCurrentContact } from '@europrocurement/l2d-modules/hooks/useCurrentContact';
import { useSnackbar } from 'notistack';
import { FormulaireFormaliteJsonld } from '@europrocurement/l2d-domain/openApi/ApiFormulaire';
import {
    CollectionDeDossierApiApiCollectionsDossiersIdValidatePutRequest,
    CollectionDeDossierCollectiondossierRead,
    CollectionDeDossierJsonldCollectiondossierCreated,
    FproPrestationJsonldCollectiondossierCreated,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import _ from 'lodash';
import { ContactJsonldTiersReadNoteGroupTimestampableBlameambleGroup } from '@europrocurement/l2d-domain/openApi/ApiTiers';
import { useFormalitiesCtx } from '@europrocurement/l2d-modules/providers';
import {
    ParcoursFormaliteContext,
    type ParcoursFormaliteContextType,
} from './ParcoursFormaliteContext';
import { createCollectionFormalite } from './functions';
import { DossierPrescriberProvider } from '../../Dossiers/providers';

export type ModalProviderType = {
    children: ReactNode;
    prescripteur?: Prescripteur;
    defaultCollectionId?: number;
    defaultDossierId?: number;
    defaultPrestaId?: number;
    defaultCurrentCatalogId?: number;
    back?: boolean;
};
export const PARCOURS_FORMALITE_CONTAINER_WIDTH = 'sm';
export const ParcoursFormaliteProvider: React.FunctionComponent<ModalProviderType> = function ({
    children,
    prescripteur = undefined,
    defaultCollectionId = -1,
    defaultDossierId = -1,
    defaultPrestaId = -1,
    defaultCurrentCatalogId = -1,
    back = false,
}) {
    const { dossierApi, collectionApi } = useFormaliteService();
    const { getFormalityById } = useFormalitiesCtx();
    const { catalogsApi } = useOffreService();

    let defaultContact = useCurrentContact();
    if (!defaultContact) {
        if (back && prescripteur && prescripteur.contacts[0]) {
            defaultContact = { ...prescripteur.contacts[0] };
        } else if (!back) {
            throw new Error('missing contact');
        }
    }
    const [contact, setContact] = useState(defaultContact);

    const xIdSociete = useSociete();

    const [collections, setCollections] = useState<ParcoursFormaliteContextType['collections']>({});
    const [dossiers, setDossiers] = useState<ParcoursFormaliteContextType['dossiers']>({});
    const [prestas, setPrestas] = useState<ParcoursFormaliteContextType['prestas']>([]);

    const [currentCollectionId, setCurrentCollectionId] = useState<number>(defaultCollectionId);
    const [currentDossierId, setCurrentDossierId] = useState<number>(defaultDossierId);
    const [currentPrestaId, setCurrentPrestaId] = useState<number>(defaultPrestaId);
    const [idFormalites, setIdFormalites] = useState<Array<number>>([]);
    const [idSousClients, setIdSousClients] = useState<Array<number>>([]);
    const [currentCatalog, setCurrentCatalog] = useState<CatalogsFull | undefined>();
    const [currentCatalogs, setCurrentCatalogs] = useState<CatalogApiObject[]>();
    const [formLoading, setFormLoading] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const sousClientDs = useTiersServiceSelector(SousClientMainSelector);
    const currentSousClient = useTiersServiceSelector(selectedSousClientSelector);

    const { selectSousClients } = useTiersService();

    const dispatch = useDispatch<TiersAppDispatch>();

    const [form, setForm] = useState<FormulaireFormaliteApiObject | null>(null);

    const [currentCatalogId, setCurrentCatalogId] = useState<number>(defaultCurrentCatalogId);

    const { formulairesApi } = useFormulaireService();

    const isFormaliteSimple = () => false; // TODO : To be implemented
    const isFormaliteCombinee = () => false; // TODO : To be implemented
    const isFormaliteMultiple = () => false; // TODO : To be implemented
    const isFormaliteDupliquee = () => false; // TODO : To be implemented
    const { enqueueSnackbar } = useSnackbar();

    if (prescripteur && !prescripteur.id) {
        enqueueSnackbar('Missing id in prescriber', { variant: 'error' });
        throw new Error('missing id in prescriber');
    }

    useEffect(() => {
        if (sousClientDs.selectedStatus !== 'loading' && idSousClients && idSousClients[0]) {
            if (
                currentSousClient &&
                currentSousClient.id &&
                !_.isEqual(idSousClients[0], currentSousClient.id)
            ) {
                dispatch(selectSousClients({ id: idSousClients[0] }));
            } else if (!currentSousClient) {
                dispatch(selectSousClients({ id: idSousClients[0] }));
            }
        }
    }, [
        dispatch,
        idSousClients,
        selectSousClients,
        currentSousClient,
        sousClientDs.selectedStatus,
    ]);

    const refreshCollection = useCallback(
        async (collectionId: number) => {
            const resCollection = await collectionApi.apiCollectionsDossiersIdGet({
                id: `${collectionId}`,
            });

            setCollections((prev) => {
                const newCollection = { ...prev };
                if (!resCollection.data.client || typeof resCollection.data.client !== 'string') {
                    enqueueSnackbar('Missing client in collection', { variant: 'error' });
                    throw new Error('missing client in collection');
                }
                newCollection[castIriToId(resCollection.data.client)] = resCollection.data;

                return { ...newCollection };
            });
        },
        [collectionApi, enqueueSnackbar],
    );

    const refreshDossier = useCallback(
        async (dossierId: number) => {
            const resDossier = await dossierApi.apiDossiersIdGet({
                id: `${dossierId}`,
            });

            if (!resDossier.data.id) {
                enqueueSnackbar('Missing id in collection', { variant: 'error' });
                throw new Error('missing id in collection');
            }
            if (!resDossier.data.client) {
                enqueueSnackbar('Missing client in collection', { variant: 'error' });
                throw new Error('missing client in collection');
            }

            setDossiers((prev) => {
                const newDossier = { ...prev };
                newDossier[castIriToId(resDossier.data.client as unknown as string)] =
                    resDossier.data;

                return { ...newDossier };
            });
        },
        [dossierApi, enqueueSnackbar],
    );

    const refreshCatalog = useCallback(
        async (catalogId: number) => {
            const resCatalog = await catalogsApi.apiCataloguesIdGet({
                id: `${catalogId}`,
            });

            setCurrentCatalog(resCatalog.data);
        },
        [catalogsApi],
    );

    const getCurrentDossier = useCallback(() => {
        if (Object.keys(dossiers).length === 1) {
            const idSousClient = Object.keys(dossiers)[0];
            return dossiers[Number(idSousClient)];
        }
        if (Object.keys(dossiers).length > 1) {
            enqueueSnackbar('formalité multiples a implementer', { variant: 'error' });
        }

        return null;
    }, [dossiers, enqueueSnackbar]);

    const getCurrentPresta = useCallback(() => {
        const curr = prestas.find(({ '@id': id }) => castIriToId(id || '') === currentPrestaId);

        return curr || null;
    }, [currentPrestaId, prestas]);

    const isNextPresta = useCallback(() => {
        const curr = prestas.find(({ '@id': id }) => castIriToId(id || '') === currentPrestaId);
        if (!curr) {
            enqueueSnackbar('next presta error', { variant: 'error' });
            throw new Error('next presta error');
        }
        const idx = prestas.indexOf(curr);
        if (idx < prestas.length - 1) {
            const nextPresta = prestas[idx + 1];
            if (!nextPresta || !nextPresta.id) {
                enqueueSnackbar('next presta error', { variant: 'error' });
                throw new Error('next presta error');
            }
            return true;
        }

        return false;
    }, [currentPrestaId, enqueueSnackbar, prestas]);

    const fetchForm = useCallback(
        async (
            innerPresta?:
                | DossierPrestationJsonldRead
                | FproPrestationJsonldCollectiondossierCreated
                | null,
        ) => {
            setForm(null);
            let currentPresta:
                | DossierPrestationJsonldRead
                | FproPrestationJsonldCollectiondossierCreated
                | undefined
                | null = innerPresta;

            if (!currentPresta) {
                currentPresta = getCurrentPresta();
            }

            if (!formLoading && currentPresta) {
                setFormLoading(true);
                const resFormality = getFormalityById(
                    castIriToId(currentPresta.formalite as unknown as string),
                );
                if (resFormality && resFormality.formId) {
                    const resFormulaire =
                        await formulairesApi.apiFormulaireFormalitesIdformaliteIdFormaliteGet({
                            id: resFormality.formId as unknown as string,
                            idFormalite: resFormality.id as unknown as string,
                        });

                    if (resFormulaire.data) {
                        setForm(resFormulaire.data as FormulaireFormaliteJsonld);
                        setFormLoading(false);
                    }
                } else {
                    setFormLoading(false);
                }
            }
        },
        [formLoading, setFormLoading, getCurrentPresta, getFormalityById, formulairesApi],
    );

    const switchToPresta = useCallback(
        async (indexPresta: number) => {
            if (indexPresta < prestas.length) {
                const newPresta = prestas[indexPresta];
                if (!newPresta || !newPresta.id) {
                    enqueueSnackbar('switch presta error', { variant: 'error' });
                    throw new Error('switch presta error');
                }
                if (newPresta.id !== currentPrestaId) {
                    setCurrentPrestaId(newPresta.id);
                    await fetchForm(newPresta);
                    setLoading(false);
                    if (newPresta.catalogue) {
                        let nextCatalogId: number = -1;
                        if (typeof newPresta.catalogue === 'string') {
                            nextCatalogId = castIriToId(newPresta.catalogue);
                        } else if (newPresta.catalogue.id) {
                            nextCatalogId = newPresta.catalogue.id;
                        }
                        refreshCatalog(nextCatalogId);
                    }
                }
                return newPresta;
            }

            return false;
        },
        [currentPrestaId, enqueueSnackbar, setCurrentPrestaId, fetchForm, prestas, refreshCatalog],
    );

    const create = useCallback(
        async (
            newIdSousClients: Array<number | undefined>,
            newIdFormalite: Array<number | undefined>,
            sousClient: SousClientApi,
            sousClientOpere: SousClientApi | undefined,
        ) => {
            let collection: CollectionDeDossierJsonldCollectiondossierCreated;
            if (!prescripteur) {
                enqueueSnackbar('missing prescripteur', { variant: 'error' });
                throw new Error('missing prescripteur');
            }
            if (!contact) {
                enqueueSnackbar('missing contact', { variant: 'error' });
                throw new Error('missing contact');
            }
            try {
                collection = await createCollectionFormalite({
                    prescripteur,
                    contact,
                    catalogs: currentCatalogs,
                    collectionApi,
                    formulairesApi,
                    xIdSociete,
                    sousClient,
                    sousClientOpere,
                });

                if (!collection.dossiers) {
                    throw new Error('missing dossiers in collection');
                }

                if (!collection.id) {
                    throw new Error('missing id collection');
                }

                if (!collection.dossiers[0].id) {
                    throw new Error('missing id dossiers');
                }

                if (!collection.dossiers[0].prestations) {
                    throw new Error('missing prestassion in dossiers');
                }

                if (!collection.dossiers[0].prestations[0]) {
                    throw new Error('missing prestassion in dossiers');
                }

                if (!collection.dossiers[0].prestations[0]['@id']) {
                    throw new Error('missing prestassion id in dossiers');
                }

                if (collection.dossiers.length > 1) {
                    throw new Error('more than 1 dossier, impossible at this step');
                }
            } catch (e) {
                enqueueSnackbar((e as Error).message, { variant: 'error' });
                throw new Error((e as Error).message);
            }

            const ids = collection.dossiers[0].prestations
                ?.filter((innetPresta) => innetPresta.formalite)
                .map((innetPresta) => castIriToId(innetPresta.formalite as string));
            if (ids) {
                setIdFormalites(ids);
            }

            if (currentSousClient?.id) {
                setIdSousClients([currentSousClient?.id]);
            }

            const prestations: ParcoursFormaliteContextType['prestas'] = collection.dossiers
                .map(
                    (dossier) =>
                        dossier.prestations as FproPrestationJsonldCollectiondossierCreated,
                )
                .flat()
                .sort((a, b) => {
                    const aFormality = getFormalityById(castIriToId(a?.formalite as string));
                    const bFormality = getFormalityById(castIriToId(b?.formalite as string));
                    const aSortIndex = aFormality?.sortOrder || Infinity;
                    const bSortIndex = bFormality?.sortOrder || Infinity;

                    if (aSortIndex === bSortIndex) {
                        return 0;
                    }

                    return aSortIndex > bSortIndex ? -1 : 1;
                });

            refreshCatalog(castIriToId(prestations[0].catalogue as string));
            setCurrentCollectionId(collection.id);
            setCurrentDossierId(collection.dossiers[0].id);
            setPrestas(prestations);
            setCurrentPrestaId(castIriToId(prestations[0]['@id'] as string));
            if (!back) {
                await Promise.all([
                    refreshCollection(collection.id),
                    refreshDossier(collection.dossiers[0].id),
                    fetchForm(prestations[0]),
                ]);
            }
            return collection;
        },
        [
            currentSousClient?.id,
            back,
            prescripteur,
            contact,
            currentCatalogs,
            collectionApi,
            formulairesApi,
            xIdSociete,
            enqueueSnackbar,
            refreshCatalog,
            refreshCollection,
            refreshDossier,
            fetchForm,
            getFormalityById,
        ],
    );

    const validate = useCallback(
        async (
            collection:
                | CollectionDeDossierCollectiondossierRead
                | CollectionDeDossierJsonldCollectiondossierCreated,
        ) => {
            if (!collection || !collection.id) {
                throw new Error('error in Collection');
            }

            const freshCollection = (
                await collectionApi.apiCollectionsDossiersIdGet({ id: `${collection.id}` })
            ).data;

            if (!freshCollection || !freshCollection.id) {
                throw new Error('missing freshCollection');
            }

            if (!freshCollection.client) {
                throw new Error('missing client');
            }
            if (!freshCollection.prescripteur) {
                throw new Error('missing prescripteur');
            }
            try {
                const request: CollectionDeDossierApiApiCollectionsDossiersIdValidatePutRequest = {
                    id: `${freshCollection?.id}`,
                    collectionDeDossierJsonldCollectiondossierValidated: {
                        prescripteur: freshCollection.prescripteur?.replace(
                            '/tiers/tiers',
                            '/formalite/tiers',
                        ),
                        urgent: freshCollection.urgent === true,
                        client: `/formalite/tiers/${freshCollection.client}`,
                        infoComp: freshCollection.infoComp,
                        libelle: freshCollection.libelle,
                    },
                };

                const res = await collectionApi.apiCollectionsDossiersIdValidatePut(request);

                return res;
            } catch (e) {
                enqueueSnackbar((e as Error).message, { variant: 'error' });

                throw new Error((e as Error).message);
            }
        },
        [collectionApi, enqueueSnackbar],
    );

    useEffect(() => {
        if (Object.keys(collections).length === 0 && currentCollectionId !== -1) {
            refreshCollection(currentCollectionId);
        }
    }, [collections, currentCollectionId, refreshCollection]);

    useEffect(() => {
        if (Object.keys(dossiers).length === 0 && currentDossierId !== -1) {
            refreshDossier(currentDossierId);
        }
    }, [currentDossierId, dossiers, refreshDossier]);

    useEffect(() => {
        if (!currentCatalog && currentCatalogId !== -1) {
            refreshCatalog(currentCatalogId);
        }
    }, [currentCatalog, currentCatalogId, refreshCatalog]);

    const parcoursFormaliteContextValue = useMemo<ParcoursFormaliteContextType>(
        () => ({
            collections,
            currentCollectionId,
            currentCatalog,
            currentCatalogs,
            currentDossierId,
            prescripteur,
            idFormalites,
            idSousClients,
            prescripteurId: prescripteur?.id,
            dossiers,
            form,
            formLoading,
            currentPrestaId,
            setCurrentPrestaId,
            prestas,
            currentSousClient,
            currentCatalogId,
            contact,
            setContact: (cont: ContactJsonldTiersReadNoteGroupTimestampableBlameambleGroup) => {
                setContact(cont);
            },
            isNextPresta,
            switchToPresta,
            getCurrentPresta,
            fetchForm,
            setForm,
            refreshDossier,
            setIdSousClients,
            setIdFormalites,
            getPrescripteur: () => prescripteur,
            getCollections: () => collections,
            getCurrentCollection: () => {
                if (Object.keys(collections).length === 1) {
                    const idSousClient = Object.keys(collections)[0];
                    return collections[Number(idSousClient)];
                }
                if (Object.keys(collections).length > 1) {
                    enqueueSnackbar('formalité multiples a implementer', { variant: 'error' });
                }
                return null;
            },
            loading,
            setLoading,
            getCurrentDossier,
            save: () => null,
            isFormaliteSimple,
            isFormaliteCombinee,
            isFormaliteMultiple,
            isFormaliteDupliquee,
            setCollections,
            create,
            validate,
            setCurrentCatalogId,
            setCurrentCatalog,
            setCurrentCatalogs,
            containerWidth: PARCOURS_FORMALITE_CONTAINER_WIDTH,
            back,
        }),
        [
            prescripteur,
            collections,
            currentCollectionId,
            currentCatalog,
            currentCatalogs,
            currentDossierId,
            idFormalites,
            idSousClients,
            dossiers,
            form,
            formLoading,
            currentPrestaId,
            prestas,
            currentSousClient,
            currentCatalogId,
            contact,
            isNextPresta,
            switchToPresta,
            getCurrentPresta,
            fetchForm,
            refreshDossier,
            loading,
            getCurrentDossier,
            create,
            validate,
            back,
            enqueueSnackbar,
        ],
    );

    return (
        <ParcoursFormaliteContext.Provider value={parcoursFormaliteContextValue}>
            <DossierPrescriberProvider>{children}</DossierPrescriberProvider>
        </ParcoursFormaliteContext.Provider>
    );
};
